import { useLazyQuery } from '@apollo/client';
import { useRouter } from 'hooks/useRouter';
import React, { useRef, useState, useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { urlSlugify } from 'utils/helper';
import debounce from 'lodash/debounce';

const SmkAsyncSelectGraphql = (props) => {
  const {
    isClearable = true,
    isValueSlugify = false,
    query,
    queryName,
    filters,
    searchKeyName,
    acceptedKey,
    acceptedValue,
    creatable,
    multi,
    placeholder,
    isDisabled,
    id,
    value,
    onChange,
    onBlur,
    dtoName,
  } = props;

  const [inputValue, setInputValue] = useState('');
  const router = useRouter();
  const latestRequestId = useRef(0);

  const [fetchOptions] = useLazyQuery(query, {
    fetchPolicy: 'network-only',
  });

  // Debounced fetch function
  const fetchOptionsDebounced = useCallback(
    debounce(async (searchValue, resolve, requestId) => {
      try {
        const processedValue = searchValue?.toLowerCase()?.trim();

        const { data } = await fetchOptions({
          variables: {
            [dtoName]: {
              [searchKeyName]: isValueSlugify ? urlSlugify(processedValue) : processedValue,
              ...filters,
            },
          },
        });

        if (requestId !== latestRequestId.current) return;

        const instances = data?.[queryName]?.results?.filter((e) => e?.id !== router?.query?.id) || [];
        const formattedOptions = instances.map((instance) => ({
          ...instance,
          label: instance[acceptedKey] || instance.title,
          value: instance[acceptedValue] || instance.id,
        }));

        resolve(formattedOptions);
      } catch (err) {
        console.error('GraphQL error:', err);
        resolve([]);
      }
    }, 300),
    [fetchOptions, queryName, filters, dtoName, searchKeyName, acceptedKey, acceptedValue, isValueSlugify]
  );

  const promiseOptions = (inputValue) => {
    if (inputValue.length <= 2) return Promise.resolve([]);
    latestRequestId.current += 1;
    const currentRequestId = latestRequestId.current;

    return new Promise((resolve) => {
      fetchOptionsDebounced(inputValue, resolve, currentRequestId);
    });
  };

  const handleChange = (newValue) => {
    if (onChange) onChange(newValue);
  };

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    return newValue;
  };

  const handleBlur = () => {
    if (onBlur) onBlur(id, true);
  };

  const IndicatorSeparator = ({ innerProps }) => (
    <span style={{ alignSelf: 'stretch', backgroundColor: 'transparent', margin: '8px 0', width: 1 }} {...innerProps} />
  );

  const commonProps = {
    id,
    isClearable,
    isMulti: multi,
    onChange: handleChange,
    onBlur: handleBlur,
    onInputChange: handleInputChange,
    loadOptions: promiseOptions,
    placeholder,
    isDisabled,
    value: value || null,
    noOptionsMessage: () => 'No Options..',
    components: { DropdownIndicator: null, IndicatorSeparator },
  };

  return creatable ? (
    <AsyncCreatableSelect inputValue={inputValue} {...commonProps} />
  ) : (
    <AsyncSelect
      {...commonProps}
      styles={{
        control: (baseStyles) => ({ ...baseStyles, border: '1px solid #ced4da' }),
        menu: (provided) => ({ ...provided, zIndex: 9999 }),
      }}
    />
  );
};

export default SmkAsyncSelectGraphql;
